import React from 'react';
import { withRouter } from 'react-router-dom';
import moment from 'moment';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import get from 'lodash/get';
import {
  FormLabel, Grid, Paper, Typography,
} from '@mui/material';
import MUIDataTable from 'mui-datatables';

import reportActions from '../../../actions/reports';
import WrappedSpinner from '../../common/wrapped-spinner';
import { MarketSelect } from '../../common/market-select';
import { BrandSelect } from '../../common/brand-select';
import DateRangeDialog from '../../common/date-range-dialog';
import withPersistentSettings from '../../common/table-with-persistent-settings';
import saveLocalSettingsWrapper from './save-report-settings';

const LOCAL_STORAGE_KEY = 'repair-ticket-page';
const styles = {
  root: {
    width: '95%',
    margin: '0 auto',
    overflowX: 'auto',
  },
};

function formatTableData(tickets) {
  const currentMoment = moment.utc().startOf('day');
  return tickets.map((ticket) => {
    const createdAt = moment.utc(ticket.createdAt).startOf('day');
    const closedAtTime = get(ticket, 'closedAt');
    const closedMoment = closedAtTime ? moment.utc(closedAtTime).startOf('day') : currentMoment;
    const daysOpen = closedMoment.diff(createdAt, 'days');
    return [
      get(ticket, 'vehicle.humanId', ''),
      get(ticket, 'category', ''),
      get(ticket, 'createdBy.name', ''),
      get(ticket, 'createdAt', ''),
      daysOpen,
      get(ticket, 'closedAt', ''),
      get(ticket, 'market.name', ''),
      get(ticket, 'status', ''),
      get(ticket, 'vehicle.brand', ''),
      get(ticket, 'vehicle.type', ''),
      get(ticket, 'description', ''),
      get(ticket, 'comments', []).map((note) => note.comment).join(','),
    ];
  });
}

class BaseRepairTicketPage extends React.Component {
  constructor(props) {
    super(props);

    const {
      selectedBrand, marketId, startDate, endDate,
    } = props;

    this.state = {
      selectedBrand,
      marketId,
      startDate: startDate ? moment.utc(startDate) : moment.utc(),
      endDate: endDate ? moment.utc(endDate) : moment.utc(),
    };

    this.handleChange = this.handleChange.bind(this);
    this.handleBrandChange = this.handleBrandChange.bind(this);
    this.handleMarketChange = this.handleMarketChange.bind(this);
    this.handleDateChange = this.handleDateChange.bind(this);
  }

  componentDidMount() {
    const { fetchRepairTicketReport } = this.props;
    const {
      startDate, endDate, selectedBrand, marketId,
    } = this.state;

    const query = {};
    if (startDate || endDate) {
      query.start_date = moment(startDate).format('x');
      query.end_date = moment(endDate).format('x');
    }
    if (selectedBrand) query.brand = selectedBrand;
    if (marketId) query.market_id = marketId;
    fetchRepairTicketReport(query);
  }

  async componentDidUpdate(prevProps, prevState) {
    const { fetchRepairTicketReport, saveReportSettings } = this.props;
    const {
      startDate, endDate, selectedBrand, marketId,
    } = this.state;

    const isStartChanged = !moment(startDate).isSame(moment(prevState.startDate));
    const isEndChanged = !moment(endDate).isSame(moment(prevState.endDate));
    const isBrandChanged = selectedBrand !== prevState.selectedBrand;
    const isMarketChanged = marketId !== prevState.marketId;

    // any state different, then re-fetch
    if (isStartChanged || isEndChanged || isBrandChanged || isMarketChanged) {
      const newSettings = {
        startDate: startDate.format(),
        endDate: endDate.format(),
        selectedBrand,
        marketId,
      };
      saveReportSettings(newSettings);

      const skipFetch = !marketId || marketId.length === 0;
      if (skipFetch) return; // don't fetch without marketId

      const query = {};
      if (startDate || endDate) {
        query.start_date = moment(startDate).format('x');
        query.end_date = moment(endDate).format('x');
      }
      if (selectedBrand) query.brand = selectedBrand;
      if (marketId) query.market_id = marketId;
      await fetchRepairTicketReport(query);
    }
  }

  get tableOptions() {
    const basicOptions = get(this, 'props.tableOptions', {});
    const localOptions = {
      rowsPerPage: 100,
      rowsPerPageOptions: [20, 50, 100],
    };

    return { ...basicOptions, ...localOptions };
  }

  get columns() {
    const { selectedFilters, primarySort, addToColumnData } = this.props;
    const columnData = [
      { name: 'Vehicle' },
      { name: 'Category' },
      { name: 'Created By', options: { filter: false } }, // FIXME https://github.com/gregnb/mui-datatables/pull/624
      {
        name: 'Created On',
        options: {
          customBodyRender: (data) => {
            if (data) {
              return <Typography noWrap>{moment(data).format('M/D/YYYY')}</Typography>;
            } else {
              return '';
            }
          },
        },
      },
      { name: 'Days Open' },
      {
        name: 'Closed On',
        options: {
          customBodyRender: (data) => {
            if (data) {
              return <Typography noWrap>{moment(data).format('M/D/YYYY')}</Typography>;
            } else {
              return '';
            }
          },
        },
      },
      { name: 'Market' },
      { name: 'Status' },
      { name: 'Brand' },
      { name: 'Type' },
      {
        name: 'Description',
        options: {
          display: 'excluded',
          viewColumns: false,
          filter: false,
        },
      },
      {
        name: 'Notes',
        options: {
          display: 'excluded',
          viewColumns: false,
          filter: false,
        },
      },
    ];
    return addToColumnData(columnData, selectedFilters, primarySort);
  }

  handleChange(event) {
    this.setState({ [event.target.name]: event.target.value });
  }

  handleDateChange(startDate, endDate) {
    this.setState({ startDate, endDate });
  }

  handleMarketChange(marketId) {
    this.setState({ marketId });
  }

  handleBrandChange(brand) {
    this.setState({ selectedBrand: brand });
  }

  render() {
    const {
      marketsLoading, reportsLoading, repairTickets, getTitleFromData,
    } = this.props;
    const {
      startDate, endDate, selectedBrand, marketId,
    } = this.state;
    const isLoading = marketsLoading || reportsLoading;
    const displayData = formatTableData(repairTickets);

    return (
      <WrappedSpinner isLoading={isLoading}>
        <Paper style={styles.root} sx={{ p: 2 }}>
          <Grid container>
            <Grid item xs={2} sx={{ mr: 2 }}>
              <MarketSelect value={marketId} handleChange={this.handleMarketChange} />
            </Grid>
            <Grid item xs={2} sx={{ mr: 2, mt: -1 }}>
              <BrandSelect value={selectedBrand} handleChange={this.handleBrandChange} />
            </Grid>
            <Grid item xs={1} sx={{ mr: 1 }}>
              <FormLabel component="legend">Start</FormLabel>
              {startDate && startDate.format('MM-DD-YYYY')}
            </Grid>
            <Grid item xs={1} sx={{ mr: 2 }}>
              <FormLabel component="legend">End</FormLabel>
              {endDate && endDate.format('MM-DD-YYYY')}
            </Grid>
            <Grid item xs={2}>
              <DateRangeDialog
                to={endDate}
                from={startDate}
                handleDateChange={this.handleDateChange}
              />
            </Grid>
            <Grid item xs={12}>
              {repairTickets && (
                <MUIDataTable
                  title={getTitleFromData('Repair Ticket Info', displayData)}
                  columns={this.columns}
                  data={displayData}
                  options={this.tableOptions}
                />
              )}
            </Grid>
          </Grid>
        </Paper>
      </WrappedSpinner>
    );
  }
}

const mapStateToProps = ({ markets, reports }) => ({
  reportsLoading: reports.isLoading,
  repairTickets: get(reports, 'repairTickets', []),
  markets: markets.markets,
  marketsLoading: markets.isLoading,
});

const mapDispatchToProps = (dispatch) => bindActionCreators({
  fetchRepairTicketReport: reportActions.fetchRepairTicketReport,
}, dispatch);

const RepairTicketPage = saveLocalSettingsWrapper(
  withPersistentSettings(
    connect(
      mapStateToProps,
      mapDispatchToProps,
    )(withRouter(BaseRepairTicketPage)),
    LOCAL_STORAGE_KEY,
  ),
  LOCAL_STORAGE_KEY,
);

export { RepairTicketPage, BaseRepairTicketPage };
